'use client';

import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

type BajaResult = {
  ok: boolean;
  /** true si era el último perfil activo: el front debe cerrar sesión */
  eraUltimoPerfil: boolean;
  error: string | null;
};

type ReactivarResult = {
  ok: boolean;
  error: string | null;
};

// Código propio que lanza desactivar_mi_perfil al rechazar un perfil de
// empresa. Desactivar el perfil no desactiva el negocio, así que la baja se
// tramita con soporte.
const ERROR_EMPRESA = 'PERFIL_EMPRESA_NO_BAJA';

const MSG_EMPRESA =
  'Para dar de baja tu negocio, escríbenos a info@bookoll.com y lo gestionamos contigo.';

export function useBajaPerfil() {
  const [loading, setLoading] = useState(false);

  /**
   * Baja lógica de un perfil del usuario autenticado.
   *
   * El aviso por correo lo dispara el trigger tr_perfil_enviar_correo_baja,
   * no el cliente. Si era el último perfil activo, la RPC crea además la
   * solicitud en solicitudes_eliminacion y avisa a info@bookoll.com.
   */
  const desactivarPerfil = useCallback(
    async (idPerfil: string): Promise<BajaResult> => {
      setLoading(true);

      try {
        if (!idPerfil) {
          return { ok: false, eraUltimoPerfil: false, error: 'Perfil no encontrado' };
        }

        const { data, error } = await supabase.rpc('desactivar_mi_perfil', {
          p_id_perfil: idPerfil,
        });

        if (error) {
          if (error.message?.includes(ERROR_EMPRESA)) {
            return { ok: false, eraUltimoPerfil: false, error: MSG_EMPRESA };
          }

          Sentry.captureException(error, { extra: { idPerfil } });
          return {
            ok: false,
            eraUltimoPerfil: false,
            error: 'No hemos podido dar de baja el perfil. Inténtalo de nuevo.',
          };
        }

        // La RPC devuelve TABLE(...), así que llega como array de una fila.
        const fila = Array.isArray(data) ? data[0] : data;

        return {
          ok: true,
          eraUltimoPerfil: fila?.era_ultimo_perfil === true,
          error: null,
        };
      } catch (e: any) {
        Sentry.captureException(e, { extra: { idPerfil } });
        return {
          ok: false,
          eraUltimoPerfil: false,
          error: e?.message ?? 'Error desconocido',
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Deshace una baja voluntaria. Devuelve ok:false si el perfil no se puede
   * reactivar, normalmente porque lo desactivó la empresa y no el usuario:
   * en ese caso no lleva fecha_baja y la RPC lo ignora.
   */
  const reactivarPerfil = useCallback(
    async (idPerfil: string): Promise<ReactivarResult> => {
      setLoading(true);

      try {
        if (!idPerfil) {
          return { ok: false, error: 'Perfil no encontrado' };
        }

        const { data, error } = await supabase.rpc('reactivar_mi_perfil', {
          p_id_perfil: idPerfil,
        });

        if (error) {
          Sentry.captureException(error, { extra: { idPerfil } });
          return {
            ok: false,
            error: 'No hemos podido reactivar el perfil. Inténtalo de nuevo.',
          };
        }

        if (data !== true) {
          return {
            ok: false,
            error: 'Este perfil no se puede reactivar. Ponte en contacto con tu empresa.',
          };
        }

        return { ok: true, error: null };
      } catch (e: any) {
        Sentry.captureException(e, { extra: { idPerfil } });
        return { ok: false, error: e?.message ?? 'Error desconocido' };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { desactivarPerfil, reactivarPerfil, loading };
}